import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Switch,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useParams } from "react-router-dom";
import {
  debtAPI,
  expenseAPI,
  goalAPI,
  incomeAPI,
  shoppingListAPI,
  shareAPI,
} from "../services/api";
import { demoService } from "../services/demo/demoService";
import { plaidService, PlaidAccount } from "../services/external/plaidService";
import { supabase } from "../services/supabase/client";
import { expensesProfilesDB } from "../services/database/expensesProfilesDB";
import { taxProfilesDB } from "../services/database/taxProfilesDB";
import { mortgageCalculatorProfilesDB } from "../services/database/mortgageCalculatorProfilesDB";
import { housingForecastProfilesDB } from "../services/database/housingForecastProfilesDB";
import { goalsGraphProfilesDB } from "../services/database/goalsGraphProfilesDB";
import SharedRecordsView, {
  SharedRecordItem,
} from "../components/groups/SharedRecordsView";
import { UnshareConfirmModal } from "../components/groups";
import { Debt, Expense, Goal, Income, ShareRecordType } from "../lib/types";

const VALID_SHARE_TYPES: ShareRecordType[] = [
  "income",
  "expense",
  "debt",
  "goal",
  "account",
  "transaction",
  "shopping",
];

type SectionState = Partial<Record<ShareRecordType, SharedRecordItem[]>>;
type SectionLoadingState = Partial<Record<ShareRecordType, boolean>>;
type SectionExpandedState = Record<ShareRecordType, boolean>;
type ToolKey =
  | "expenses"
  | "shoppingLists"
  | "taxCalculator"
  | "mortgageCalculator"
  | "housingForecast"
  | "goalsGraph";
type ToolProfile = { id: string; name: string };
type ToolProfileCatalog = Record<ToolKey, ToolProfile[]>;
type ToolProfileShareState = Record<ToolKey, Record<string, boolean>>;

const INITIAL_EXPANDED_STATE: SectionExpandedState = {
  income: false,
  expense: false,
  debt: false,
  goal: false,
  account: false,
  transaction: false,
  shopping: false,
};

const GroupSharedPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [unsharingId, setUnsharingId] = useState<string | null>(null);
  const [confirmShareId, setConfirmShareId] = useState<string | null>(null);
  const [shareAllLoading, setShareAllLoading] =
    useState<ShareRecordType | null>(null);
  const [shareAllEnabled, setShareAllEnabled] = useState<
    Partial<Record<ShareRecordType, boolean>>
  >({});
  const [shareAllStatusHydrating, setShareAllStatusHydrating] = useState(true);
  const [sectionRecords, setSectionRecords] = useState<SectionState>({});
  const [sectionLoading, setSectionLoading] = useState<SectionLoadingState>({});
  const [expandedSections, setExpandedSections] =
    useState<SectionExpandedState>(INITIAL_EXPANDED_STATE);
  const [incomeGroupExpanded, setIncomeGroupExpanded] = useState<
    Record<string, boolean>
  >({});
  const [incomeGroupLoading, setIncomeGroupLoading] = useState<
    Record<string, boolean>
  >({});
  const [expenseGroupExpanded, setExpenseGroupExpanded] = useState<
    Record<string, boolean>
  >({});
  const [expenseGroupLoading, setExpenseGroupLoading] = useState<
    Record<string, boolean>
  >({});
  const [incomeItems, setIncomeItems] = useState<Income[]>([]);
  const [expenseItems, setExpenseItems] = useState<Expense[]>([]);
  const [debtItems, setDebtItems] = useState<Debt[]>([]);
  const [debtPage, setDebtPage] = useState(0);
  const [goalItems, setGoalItems] = useState<Goal[]>([]);
  const [accountItems, setAccountItems] = useState<PlaidAccount[]>([]);
  const [accountCustomNames, setAccountCustomNames] = useState<
    Record<string, string>
  >({});
  const [incomeLoading, setIncomeLoading] = useState(false);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [debtLoading, setDebtLoading] = useState(false);
  const [goalLoading, setGoalLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [toolProfilesLoading, setToolProfilesLoading] = useState(false);
  const [toolProfiles, setToolProfiles] = useState<ToolProfileCatalog>({
    expenses: [],
    shoppingLists: [],
    taxCalculator: [],
    mortgageCalculator: [],
    housingForecast: [],
    goalsGraph: [],
  });
  const [toolProfileShares, setToolProfileShares] =
    useState<ToolProfileShareState>({
      expenses: {},
      shoppingLists: {},
      taxCalculator: {},
      mortgageCalculator: {},
      housingForecast: {},
      goalsGraph: {},
    });

  const toolProfileStorageKey = id
    ? `group_tool_profile_shares_${id}`
    : "group_tool_profile_shares_unknown";

  const withDemoFallback = useCallback(
    (profiles: ToolProfile[], tool: ToolKey): ToolProfile[] => {
      if (!demoService.isDemo()) {
        return profiles;
      }
      if (profiles.length > 0) {
        return profiles;
      }
      return [{ id: `demo-${tool}`, name: "Demo" }];
    },
    []
  );

  const loadToolProfiles = useCallback(async () => {
    try {
      setToolProfilesLoading(true);
      const [
        expenseProfiles,
        shoppingLists,
        taxProfiles,
        mortgageProfiles,
        housingProfiles,
        goalsProfiles,
      ] = await Promise.all([
        expensesProfilesDB.getAll(),
        shoppingListAPI.getAll(),
        taxProfilesDB.getAll(),
        mortgageCalculatorProfilesDB.getAll(),
        housingForecastProfilesDB.getAll(),
        goalsGraphProfilesDB.getAll(),
      ]);

      setToolProfiles({
        expenses: withDemoFallback(
          expenseProfiles.map((p) => ({ id: p.id, name: p.name })),
          "expenses"
        ),
        shoppingLists: shoppingLists.map((list) => ({
          id: list.id,
          name: list.title,
        })),
        taxCalculator: withDemoFallback(
          taxProfiles.map((p) => ({ id: p.id, name: p.name })),
          "taxCalculator"
        ),
        mortgageCalculator: withDemoFallback(
          mortgageProfiles.map((p) => ({ id: p.id, name: p.name })),
          "mortgageCalculator"
        ),
        housingForecast: withDemoFallback(
          housingProfiles.map((p) => ({ id: p.id, name: p.name })),
          "housingForecast"
        ),
        goalsGraph: withDemoFallback(
          goalsProfiles.map((p) => ({ id: p.id, name: p.name })),
          "goalsGraph"
        ),
      });
    } catch (error) {
      console.error("Failed loading tool profiles for sharing:", error);
      setToolProfiles({
        expenses: withDemoFallback([], "expenses"),
        shoppingLists: [],
        taxCalculator: withDemoFallback([], "taxCalculator"),
        mortgageCalculator: withDemoFallback([], "mortgageCalculator"),
        housingForecast: withDemoFallback([], "housingForecast"),
        goalsGraph: withDemoFallback([], "goalsGraph"),
      });
    } finally {
      setToolProfilesLoading(false);
    }
  }, [withDemoFallback]);

  const loadToolProfileShareState = useCallback(() => {
    try {
      const raw = localStorage.getItem(toolProfileStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<ToolProfileShareState>;
      setToolProfileShares((prev) => ({
        expenses: parsed.expenses || prev.expenses,
        shoppingLists: prev.shoppingLists,
        taxCalculator: parsed.taxCalculator || prev.taxCalculator,
        mortgageCalculator:
          parsed.mortgageCalculator || prev.mortgageCalculator,
        housingForecast: parsed.housingForecast || prev.housingForecast,
        goalsGraph: parsed.goalsGraph || prev.goalsGraph,
      }));
    } catch (error) {
      console.error("Failed loading tool profile share state:", error);
    }
  }, [toolProfileStorageKey]);

  const persistToolProfileShareState = useCallback(
    (next: ToolProfileShareState) => {
      try {
        localStorage.setItem(toolProfileStorageKey, JSON.stringify(next));
      } catch (error) {
        console.error("Failed saving tool profile share state:", error);
      }
    },
    [toolProfileStorageKey]
  );

  const mapShares = useCallback((shares: any[]): SharedRecordItem[] => {
    return shares
      .map((share: any) => {
        const recordType = String(share?.recordType ?? share?.record_type ?? "")
          .toLowerCase()
          .trim() as ShareRecordType;
        const recordId = String(share?.recordId ?? share?.record_id ?? "");
        const recordLabel = String(
          share?.recordLabel ?? share?.record_label ?? ""
        ).trim();
        const shareId = String(share?.id ?? `${recordType}-${recordId}`);
        const permission = String(share?.permission ?? "view");

        if (!VALID_SHARE_TYPES.includes(recordType) || !recordId) {
          return null;
        }

        return {
          id: shareId,
          label: recordLabel || `${recordType} ${recordId.slice(0, 8)}`,
          recordId,
          recordType,
          permission,
        } as SharedRecordItem;
      })
      .filter((item): item is SharedRecordItem => item !== null);
  }, []);

  useEffect(() => {
    loadToolProfileShareState();
    void loadToolProfiles();
  }, [loadToolProfileShareState, loadToolProfiles]);

  const loadIncomeItems = useCallback(async () => {
    try {
      setIncomeLoading(true);
      const allIncome = await incomeAPI.getAll();
      setIncomeItems(allIncome.filter((income) => Boolean(income?.id)));
    } catch (error) {
      console.error("Failed to load income items for sharing:", error);
      setIncomeItems([]);
    } finally {
      setIncomeLoading(false);
    }
  }, []);

  const loadExpenseItems = useCallback(async () => {
    try {
      setExpenseLoading(true);
      const allExpenses = await expenseAPI.getAll();
      setExpenseItems(allExpenses.filter((expense) => Boolean(expense?.id)));
    } catch (error) {
      console.error("Failed to load expense items for sharing:", error);
      setExpenseItems([]);
    } finally {
      setExpenseLoading(false);
    }
  }, []);

  const loadDebtItems = useCallback(async () => {
    try {
      setDebtLoading(true);
      const allDebts = await debtAPI.getAll();
      setDebtItems(allDebts.filter((debt) => Boolean(debt?.id)));
    } catch (error) {
      console.error("Failed to load debt items for sharing:", error);
      setDebtItems([]);
    } finally {
      setDebtLoading(false);
    }
  }, []);

  const loadGoalItems = useCallback(async () => {
    try {
      setGoalLoading(true);
      const allGoals = await goalAPI.getAll();
      setGoalItems(allGoals.filter((goal) => Boolean(goal?.id)));
    } catch (error) {
      console.error("Failed to load goal items for sharing:", error);
      setGoalItems([]);
    } finally {
      setGoalLoading(false);
    }
  }, []);

  const loadAccountItems = useCallback(async () => {
    try {
      setAccountLoading(true);
      try {
        const names = await plaidService.getCustomNames();
        setAccountCustomNames(names || {});
      } catch (nameError) {
        console.error(
          "Failed loading custom account names for sharing:",
          nameError
        );
        setAccountCustomNames({});
      }
      let allAccounts = await plaidService.getAccounts();
      if (!Array.isArray(allAccounts) || allAccounts.length === 0) {
        const cachedAccounts = await plaidService.getAccountsCached();
        if (Array.isArray(cachedAccounts) && cachedAccounts.length > 0) {
          allAccounts = cachedAccounts;
        } else {
          const refreshedAccounts = await plaidService.refreshAccountsCache();
          if (Array.isArray(refreshedAccounts)) {
            allAccounts = refreshedAccounts;
          }
        }
      }
      if (!Array.isArray(allAccounts) || allAccounts.length === 0) {
        const { data: mappedAccounts, error: mappedAccountsError } =
          await supabase
            .from("plaid_accounts")
            .select("plaid_account_id")
            .order("created_at", { ascending: true });
        if (mappedAccountsError) {
          console.error(
            "Failed loading plaid_accounts fallback for sharing:",
            mappedAccountsError
          );
        } else {
          const mapped = (mappedAccounts || [])
            .map((row) => String(row?.plaid_account_id || "").trim())
            .filter((accountId) => accountId.length > 0)
            .map(
              (accountId) =>
                ({
                  account_id: accountId,
                  item_id: "",
                  balances: {
                    available: null,
                    current: null,
                    limit: null,
                  },
                  mask: accountId.slice(-4),
                  name: `Account #${accountId.slice(-6)}`,
                  official_name: null,
                  subtype: "unknown",
                  type: "depository",
                }) as PlaidAccount
            );
          if (mapped.length > 0) {
            allAccounts = mapped;
          }
        }
      }
      setAccountItems(
        (allAccounts || []).filter((account) => Boolean(account?.account_id))
      );
    } catch (error) {
      console.error("Failed to load account items for sharing:", error);
      try {
        const cachedAccounts = await plaidService.getAccountsCached();
        if (Array.isArray(cachedAccounts) && cachedAccounts.length > 0) {
          setAccountItems(
            cachedAccounts.filter((account) => Boolean(account?.account_id))
          );
          return;
        }
      } catch (fallbackError) {
        console.error(
          "Failed to load cached account items for sharing:",
          fallbackError
        );
      }
    } finally {
      setAccountLoading(false);
    }
  }, []);

  const loadSection = useCallback(
    async (recordType: ShareRecordType, force = false) => {
      if (!id) return;
      if (!force && sectionRecords[recordType]) return;

      setSectionLoading((prev) => ({ ...prev, [recordType]: true }));
      try {
        const shares = await shareAPI.getSharesByGroup(id);
        const mapped = mapShares(shares).filter(
          (item) => item.recordType === recordType
        );
        setSectionRecords((prev) => ({ ...prev, [recordType]: mapped }));
      } catch (error) {
        console.error("Failed to load shared records section:", {
          recordType,
          error,
        });
        setSectionRecords((prev) => ({ ...prev, [recordType]: [] }));
      } finally {
        setSectionLoading((prev) => ({ ...prev, [recordType]: false }));
      }
    },
    [id, mapShares, sectionRecords]
  );

  const hydrateSharesState = useCallback(async () => {
    if (!id) return;
    setShareAllStatusHydrating(true);
    try {
      const shares = await shareAPI.getSharesByGroup(id);
      let statuses: Partial<Record<ShareRecordType, boolean>> = {};
      try {
        statuses = await shareAPI.getShareAllStatus(id);
      } catch (error) {
        console.error(
          "Failed to load share-all status, using share rows:",
          error
        );
      }
      const mapped = mapShares(shares);
      const nextSections: SectionState = {};
      VALID_SHARE_TYPES.forEach((type) => {
        nextSections[type] = mapped.filter((item) => item.recordType === type);
      });
      setSectionRecords(nextSections);
      const nextShoppingLists = mapped
        .filter((item) => item.recordType === "shopping")
        .reduce<Record<string, boolean>>((acc, item) => {
          if (!item.recordId) return acc;
          acc[item.recordId] = true;
          return acc;
        }, {});
      setToolProfileShares((prev) => ({
        ...prev,
        shoppingLists: nextShoppingLists,
      }));
      const nextEnabled: Partial<Record<ShareRecordType, boolean>> = {};
      VALID_SHARE_TYPES.forEach((type) => {
        nextEnabled[type] =
          Boolean(statuses?.[type]) || Boolean(nextSections[type]?.length);
      });
      setShareAllEnabled(nextEnabled);
    } finally {
      setShareAllStatusHydrating(false);
    }
  }, [id, mapShares]);

  const shareAll = useCallback(
    async (recordType: ShareRecordType, enabled: boolean) => {
      if (!id) return;

      try {
        setShareAllLoading(recordType);
        if (enabled) {
          console.log("[GroupSharedPage] Share all enabled", {
            groupId: id,
            recordType,
          });
          await shareAPI.shareAllByType({
            groupId: id,
            recordType,
            permission: "view",
          });
        } else {
          await shareAPI.unshareAllByType({
            groupId: id,
            recordType,
          });
        }
        await hydrateSharesState();
        if (recordType === "income") {
          await loadIncomeItems();
        } else if (recordType === "expense") {
          await loadExpenseItems();
        }
      } catch (error) {
        console.error("Failed to share all:", error);
      } finally {
        setShareAllLoading(null);
      }
    },
    [id, hydrateSharesState, loadIncomeItems, loadExpenseItems]
  );

  const isIncomeSharedByRecordId = useCallback(
    (recordId: string) =>
      (sectionRecords.income || []).find(
        (record) => record.recordId === recordId
      ),
    [sectionRecords.income]
  );

  const isExpenseSharedByRecordId = useCallback(
    (recordId: string) =>
      (sectionRecords.expense || []).find(
        (record) => record.recordId === recordId
      ),
    [sectionRecords.expense]
  );

  const isDebtSharedByRecordId = useCallback(
    (recordId: string) =>
      (sectionRecords.debt || []).find(
        (record) => record.recordId === recordId
      ),
    [sectionRecords.debt]
  );

  const isAccountSharedByRecordId = useCallback(
    (recordId: string) =>
      (sectionRecords.account || []).find(
        (record) => record.recordId === recordId
      ),
    [sectionRecords.account]
  );

  const isShoppingListSharedByRecordId = useCallback(
    (recordId: string) =>
      (sectionRecords.shopping || []).find(
        (record) => record.recordId === recordId
      ),
    [sectionRecords.shopping]
  );

  const toggleIncomeShare = useCallback(
    async (income: Income, enabled: boolean) => {
      if (!id || !income?.id) return;
      try {
        if (enabled) {
          await shareAPI.shareRecord({
            recordType: "income",
            recordId: income.id,
            groupIds: [id],
            permission: "view",
          });
        } else {
          const share = isIncomeSharedByRecordId(income.id);
          if (share) {
            await shareAPI.unshare(share.id);
          }
        }
        await loadSection("income", true);
      } catch (error) {
        console.error("Failed toggling income share:", error);
      }
    },
    [id, isIncomeSharedByRecordId, loadSection]
  );

  const toggleExpenseShare = useCallback(
    async (expense: Expense, enabled: boolean) => {
      if (!id || !expense?.id) return;
      try {
        if (enabled) {
          await shareAPI.shareRecord({
            recordType: "expense",
            recordId: expense.id,
            groupIds: [id],
            permission: "view",
          });
        } else {
          const share = isExpenseSharedByRecordId(expense.id);
          if (share) {
            await shareAPI.unshare(share.id);
          }
        }
        await loadSection("expense", true);
      } catch (error) {
        console.error("Failed toggling expense share:", error);
      }
    },
    [id, isExpenseSharedByRecordId, loadSection]
  );

  const toggleDebtShare = useCallback(
    async (debt: Debt, enabled: boolean) => {
      if (!id || !debt?.id) return;
      try {
        if (enabled) {
          await shareAPI.shareRecord({
            recordType: "debt",
            recordId: debt.id,
            groupIds: [id],
            permission: "view",
          });
        } else {
          const share = isDebtSharedByRecordId(debt.id);
          if (share) {
            await shareAPI.unshare(share.id);
          }
        }
        await loadSection("debt", true);
      } catch (error) {
        console.error("Failed toggling debt share:", error);
      }
    },
    [id, isDebtSharedByRecordId, loadSection]
  );

  const toggleAccountShare = useCallback(
    async (account: PlaidAccount, enabled: boolean) => {
      if (!id || !account?.account_id) return;
      try {
        if (enabled) {
          await shareAPI.shareRecord({
            recordType: "account",
            recordId: account.account_id,
            groupIds: [id],
            permission: "view",
          });
        } else {
          const share = isAccountSharedByRecordId(account.account_id);
          if (share) {
            await shareAPI.unshare(share.id);
          }
        }
        await loadSection("account", true);
      } catch (error) {
        console.error("Failed toggling account share:", error);
      }
    },
    [id, isAccountSharedByRecordId, loadSection]
  );

  const toggleShoppingListShare = useCallback(
    async (shoppingListId: string, enabled: boolean) => {
      if (!id || !shoppingListId) return;
      try {
        if (enabled) {
          await shareAPI.shareRecord({
            recordType: "shopping",
            recordId: shoppingListId,
            groupIds: [id],
            permission: "view",
          });
        } else {
          const share = isShoppingListSharedByRecordId(shoppingListId);
          if (share) {
            await shareAPI.unshare(share.id);
          }
        }
        await hydrateSharesState();
      } catch (error) {
        console.error("Failed toggling shopping list share:", error);
      }
    },
    [id, isShoppingListSharedByRecordId, hydrateSharesState]
  );

  const toggleIncomeGroupShare = useCallback(
    async (group: { payee: string; items: Income[] }, enabled: boolean) => {
      if (!id) return;
      const groupKey = group.payee;
      try {
        setIncomeGroupLoading((prev) => ({ ...prev, [groupKey]: true }));
        if (enabled) {
          const rowsToShare = group.items.filter(
            (income) => income?.id && !isIncomeSharedByRecordId(income.id)
          );
          await Promise.all(
            rowsToShare.map((income) =>
              shareAPI.shareRecord({
                recordType: "income",
                recordId: income.id,
                groupIds: [id],
                permission: "view",
              })
            )
          );
        } else {
          const sharesToRemove = group.items
            .map((income) => isIncomeSharedByRecordId(income.id))
            .filter((share): share is SharedRecordItem => Boolean(share));
          await Promise.all(
            sharesToRemove.map((share) => shareAPI.unshare(share.id))
          );
        }
        await loadSection("income", true);
      } catch (error) {
        console.error("Failed toggling income group share:", error);
      } finally {
        setIncomeGroupLoading((prev) => ({ ...prev, [groupKey]: false }));
      }
    },
    [id, isIncomeSharedByRecordId, loadSection]
  );

  const toggleExpenseGroupShare = useCallback(
    async (group: { name: string; items: Expense[] }, enabled: boolean) => {
      if (!id) return;
      const groupKey = group.name;
      try {
        setExpenseGroupLoading((prev) => ({ ...prev, [groupKey]: true }));
        if (enabled) {
          const rowsToShare = group.items.filter(
            (expense) => expense?.id && !isExpenseSharedByRecordId(expense.id)
          );
          await Promise.all(
            rowsToShare.map((expense) =>
              shareAPI.shareRecord({
                recordType: "expense",
                recordId: expense.id,
                groupIds: [id],
                permission: "view",
              })
            )
          );
        } else {
          const sharesToRemove = group.items
            .map((expense) => isExpenseSharedByRecordId(expense.id))
            .filter((share): share is SharedRecordItem => Boolean(share));
          await Promise.all(
            sharesToRemove.map((share) => shareAPI.unshare(share.id))
          );
        }
        await loadSection("expense", true);
      } catch (error) {
        console.error("Failed toggling expense group share:", error);
      } finally {
        setExpenseGroupLoading((prev) => ({ ...prev, [groupKey]: false }));
      }
    },
    [id, isExpenseSharedByRecordId, loadSection]
  );

  const groupedIncomeItems = useMemo(() => {
    const groups = new Map<string, Income[]>();
    incomeItems.forEach((income) => {
      const payee = String(income.client || "").trim() || "Untitled";
      if (!groups.has(payee)) {
        groups.set(payee, []);
      }
      groups.get(payee)?.push(income);
    });
    return Array.from(groups.entries())
      .map(([payee, items]) => ({
        payee,
        items: [...items].sort((a, b) =>
          (a.id || "").localeCompare(b.id || "")
        ),
      }))
      .sort((a, b) => a.payee.localeCompare(b.payee));
  }, [incomeItems]);

  const sharedIncomeRows = useMemo(() => {
    type IncomeRow = {
      id: string;
      payee: string;
    };

    if (incomeItems.length > 0) {
      return incomeItems
        .filter((income) => Boolean(income?.id))
        .map((income) => ({
          id: String(income.id),
          payee:
            String(income.client || "").trim() ||
            `Income #${String(income.id).slice(-6)}`,
        }))
        .sort((a, b) => a.payee.localeCompare(b.payee));
    }

    return (sectionRecords.income || [])
      .map((record) => {
        const id = String(record.recordId || record.id || "").trim();
        if (!id) return null;
        const label = String(record.label || "").trim();
        const scrubbedLabel = /^income(\s|$)/i.test(label) ? "" : label;
        return {
          id,
          payee: scrubbedLabel || `Income #${id.slice(-6)}`,
        };
      })
      .filter((row): row is IncomeRow => Boolean(row));
  }, [incomeItems, sectionRecords.income]);

  const groupedExpenseItems = useMemo(() => {
    const sourceExpenses: Array<Pick<Expense, "id" | "name">> =
      expenseItems.length > 0
        ? expenseItems
        : (sectionRecords.expense || [])
            .map((record) => {
              const fallbackName = String(record.label || "").trim();
              const normalizedName = /^expense(\s|$)/i.test(fallbackName)
                ? ""
                : fallbackName;
              const id = String(record.recordId || "").trim();
              if (!id) {
                return null;
              }
              return {
                id,
                name: normalizedName || `Expense #${id.slice(-6)}`,
              };
            })
            .filter(
              (item): item is Pick<Expense, "id" | "name"> => item !== null
            );

    const groups = new Map<string, Expense[]>();
    sourceExpenses.forEach((expense) => {
      const name = String(expense.name || "").trim() || "Untitled";
      if (!groups.has(name)) {
        groups.set(name, []);
      }
      groups.get(name)?.push(expense as Expense);
    });
    return Array.from(groups.entries())
      .map(([name, items]) => ({
        name,
        items: [...items].sort((a, b) =>
          String(a.id || "").localeCompare(String(b.id || ""))
        ),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [expenseItems, sectionRecords.expense]);

  const sharedDebtRows = useMemo(() => {
    type DebtRow = {
      id: string;
      creditor: string;
      account: string;
      balanceLabel: string;
    };

    if (debtItems.length > 0) {
      return debtItems
        .filter((debt) => Boolean(debt?.id))
        .map((debt) => ({
          id: String(debt.id),
          creditor: String(debt.creditor || "").trim() || "Unknown",
          account:
            String(debt.account || "").trim() ||
            String(debt.type || "").trim() ||
            "Unknown",
          balanceLabel: `$${Math.abs(Number(debt.balance || 0)).toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}`,
        }))
        .sort((a, b) => {
          const creditorCmp = a.creditor.localeCompare(b.creditor);
          if (creditorCmp !== 0) return creditorCmp;
          return a.account.localeCompare(b.account);
        });
    }

    return (sectionRecords.debt || [])
      .map((record) => {
        const label = String(record.label || "").trim();
        const matched = label.match(
          /^(.*?)\s*>\s*(.*?)\s*\$([0-9,]+(?:\.[0-9]{2})?)$/
        );
        if (matched) {
          return {
            id: String(record.recordId || record.id),
            creditor: matched[1].trim() || "Unknown",
            account: matched[2].trim() || "Unknown",
            balanceLabel: `$${matched[3]}`,
          };
        }
        return {
          id: String(record.recordId || record.id),
          creditor: "Unknown",
          account: label || "Unknown",
          balanceLabel: "$0.00",
        };
      })
      .filter((row) => Boolean(row.id));
  }, [debtItems, sectionRecords.debt]);

  const pagedSharedDebtRows = useMemo(() => {
    const rowsPerPage = 5;
    const start = debtPage * rowsPerPage;
    return sharedDebtRows.slice(start, start + rowsPerPage);
  }, [debtPage, sharedDebtRows]);

  useEffect(() => {
    const rowsPerPage = 5;
    const maxPage = Math.max(
      Math.ceil(sharedDebtRows.length / rowsPerPage) - 1,
      0
    );
    if (debtPage > maxPage) {
      setDebtPage(maxPage);
    }
  }, [debtPage, sharedDebtRows.length]);

  const sharedAccountRows = useMemo(() => {
    type AccountRow = {
      id: string;
      name: string;
    };

    if (accountItems.length > 0) {
      return accountItems
        .filter((account) => Boolean(account?.account_id))
        .map((account) => ({
          id: String(account.account_id),
          name:
            String(accountCustomNames[account.account_id] || "").trim() ||
            String(account.name || "").trim() ||
            String(account.official_name || "").trim() ||
            `Account #${String(account.account_id).slice(-6)}`,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    return (sectionRecords.account || [])
      .map((record) => {
        const id = String(record.recordId || record.id || "").trim();
        if (!id) return null;
        const label = String(record.label || "").trim();
        const scrubbedLabel = /^account(\s|$)/i.test(label) ? "" : label;
        return {
          id,
          name:
            scrubbedLabel ||
            String(accountCustomNames[id] || "").trim() ||
            `Account #${id.slice(-6)}`,
        };
      })
      .filter((row): row is AccountRow => Boolean(row));
  }, [accountItems, accountCustomNames, sectionRecords.account]);

  useEffect(() => {
    let active = true;
    if (!id) return () => undefined;
    (async () => {
      try {
        await hydrateSharesState();
        await loadExpenseItems();
        await loadDebtItems();
        await loadAccountItems();
      } catch (error) {
        if (!active) return;
        console.error("Failed to hydrate shared records state:", error);
      }
    })();
    return () => {
      active = false;
    };
  }, [
    id,
    hydrateSharesState,
    loadExpenseItems,
    loadDebtItems,
    loadAccountItems,
  ]);

  const toolSections = useMemo(
    () =>
      [
        { key: "expenses" as const, label: "Expenses Tool Profiles" },
        {
          key: "shoppingLists" as const,
          label: "Shopping & Groceries Lists",
        },
        { key: "taxCalculator" as const, label: "Tax Calculator Profiles" },
        {
          key: "mortgageCalculator" as const,
          label: "Mortgage Calculator Profiles",
        },
        { key: "housingForecast" as const, label: "Housing Forecast Profiles" },
        { key: "goalsGraph" as const, label: "Goals Graph Profiles" },
      ] as Array<{ key: ToolKey; label: string }>,
    []
  );

  const toggleToolProfileShare = useCallback(
    (tool: ToolKey, profileId: string, enabled: boolean) => {
      setToolProfileShares((prev) => {
        const next = {
          ...prev,
          [tool]: {
            ...prev[tool],
            [profileId]: enabled,
          },
        };
        persistToolProfileShareState(next);
        return next;
      });
    },
    [persistToolProfileShareState]
  );

  const handleUnshare = useCallback((shareId: string) => {
    setConfirmShareId(shareId);
  }, []);

  const confirmUnshare = useCallback(async () => {
    if (!confirmShareId) return;

    try {
      setUnsharingId(confirmShareId);
      await shareAPI.unshare(confirmShareId);
      setSectionRecords((prev) => {
        const next = { ...prev };
        (Object.keys(next) as ShareRecordType[]).forEach((type) => {
          next[type] = (next[type] || []).filter(
            (record) => record.id !== confirmShareId
          );
        });
        return next;
      });
    } catch (error) {
      console.error("Failed to unshare record:", error);
    } finally {
      setUnsharingId(null);
      setConfirmShareId(null);
    }
  }, [confirmShareId]);

  const renderSection = useCallback(
    (title: string, recordType: ShareRecordType, description?: string) => {
      const isExpanded = Boolean(expandedSections[recordType]);
      const isLoading = Boolean(sectionLoading[recordType]);
      const records = sectionRecords[recordType] || [];
      const isShareAllOn = Boolean(shareAllEnabled[recordType]);
      const shareAllTooltip = isShareAllOn
        ? "Sharing all items."
        : "Toggle On to Share All Items";
      const showShareToggleLoadingAnimation =
        shareAllStatusHydrating && shareAllEnabled[recordType] === undefined;

      return (
        <Accordion
          key={recordType}
          disableGutters
          expanded={isExpanded}
          onChange={(_, expanded) => {
            setExpandedSections((prev) => ({
              ...prev,
              [recordType]: expanded,
            }));
            if (expanded) {
              void loadSection(recordType);
              if (recordType === "income") {
                void loadIncomeItems();
              } else if (recordType === "expense") {
                void loadExpenseItems();
              } else if (recordType === "debt") {
                void loadDebtItems();
              } else if (recordType === "account") {
                void loadAccountItems();
              }
            }
          }}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            "&:before": { display: "none" },
            boxShadow: "none",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
              spacing={1}
              sx={{ width: "100%", pr: 1 }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {title}
                </Typography>
                {description ? (
                  <Typography variant="body2" color="text.secondary">
                    {description}
                  </Typography>
                ) : null}
              </Box>
              <Tooltip title={shareAllTooltip}>
                <span>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      "@keyframes shareToggleBreathing": {
                        "0%": { opacity: 0.45 },
                        "50%": { opacity: 1 },
                        "100%": { opacity: 0.45 },
                      },
                      animation: showShareToggleLoadingAnimation
                        ? "shareToggleBreathing 1.25s ease-in-out infinite"
                        : "none",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Toggle Sharing All
                    </Typography>
                    <Switch
                      checked={isShareAllOn}
                      disabled={shareAllLoading === recordType}
                      onChange={(_, checked) => {
                        void shareAll(recordType, checked);
                      }}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#14b8a6",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#14b8a6",
                          },
                      }}
                    />
                  </Stack>
                </span>
              </Tooltip>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Divider sx={{ mb: 1.5 }} />
            {isLoading ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ py: 1, color: "text.secondary" }}
              >
                <CircularProgress size={18} />
                <Typography variant="body2">Loading shared items...</Typography>
              </Stack>
            ) : (
              <Stack spacing={2}>
                {recordType === "income" ? (
                  <Box>
                    {incomeLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Loading income items...
                        </Typography>
                      </Stack>
                    ) : groupedIncomeItems.length === 0 ? (
                      <Typography color="text.secondary">
                        No items available.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Group</TableCell>
                            <TableCell>Income</TableCell>
                            <TableCell align="right">Share</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedIncomeItems.flatMap((group) =>
                            group.items.map((income) => {
                              const shared = Boolean(
                                isIncomeSharedByRecordId(income.id)
                              );
                              return (
                                <TableRow key={income.id}>
                                  <TableCell>{group.payee}</TableCell>
                                  <TableCell>
                                    {`${group.payee} (#${String(income.id).slice(-6)})`}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Switch
                                      checked={isShareAllOn ? true : shared}
                                      disabled={isShareAllOn}
                                      onChange={(_, checked) => {
                                        void toggleIncomeShare(income, checked);
                                      }}
                                      sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                          color: "#14b8a6",
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                          {
                                            backgroundColor: "#14b8a6",
                                          },
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                ) : null}
                {recordType === "expense" ? (
                  <Box>
                    {expenseLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Loading expense items...
                        </Typography>
                      </Stack>
                    ) : groupedExpenseItems.length === 0 ? (
                      <Typography color="text.secondary">
                        No items available.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Group</TableCell>
                            <TableCell>Expense</TableCell>
                            <TableCell align="right">Share</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedExpenseItems.flatMap((group) =>
                            group.items.map((expense) => {
                              const shared = Boolean(
                                isExpenseSharedByRecordId(expense.id)
                              );
                              return (
                                <TableRow key={expense.id}>
                                  <TableCell>{group.name}</TableCell>
                                  <TableCell>
                                    {`${group.name} (#${String(expense.id).slice(-6)})`}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Switch
                                      checked={isShareAllOn ? true : shared}
                                      disabled={isShareAllOn}
                                      onChange={(_, checked) => {
                                        void toggleExpenseShare(
                                          expense as Expense,
                                          checked
                                        );
                                      }}
                                      sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                          color: "#14b8a6",
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                          {
                                            backgroundColor: "#14b8a6",
                                          },
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                ) : null}
                {recordType === "debt" ? (
                  <Box>
                    {debtLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Loading debt items...
                        </Typography>
                      </Stack>
                    ) : sharedDebtRows.length === 0 ? (
                      <Typography color="text.secondary">
                        No items shared with this group yet.
                      </Typography>
                    ) : (
                      <Box>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Creditor</TableCell>
                              <TableCell>Account</TableCell>
                              <TableCell align="right">Balance</TableCell>
                              <TableCell align="right">Share</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pagedSharedDebtRows.map((row) => {
                              const shared = Boolean(
                                isDebtSharedByRecordId(row.id)
                              );
                              return (
                                <TableRow key={row.id}>
                                  <TableCell>{row.creditor}</TableCell>
                                  <TableCell>{row.account}</TableCell>
                                  <TableCell align="right">
                                    {row.balanceLabel}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Switch
                                      checked={shared}
                                      disabled={isShareAllOn}
                                      onChange={(_, checked) => {
                                        const debt =
                                          debtItems.find(
                                            (item) => item.id === row.id
                                          ) ||
                                          ({
                                            id: row.id,
                                            creditor: row.creditor,
                                            account: row.account,
                                            balance: Number(
                                              String(row.balanceLabel)
                                                .replace(/[$,]/g, "")
                                                .trim()
                                            ),
                                            type: "",
                                            payoffStrategy: "",
                                            creditAffecting: "",
                                            creditLimit: 0,
                                            paydownAmount: 0,
                                            maxMonthlyPayment: 0,
                                            dueDate: "",
                                            status: "",
                                            statementCredits: 0,
                                          } as Debt);
                                        void toggleDebtShare(debt, checked);
                                      }}
                                      sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                          color: "#14b8a6",
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                          {
                                            backgroundColor: "#14b8a6",
                                          },
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        <TablePagination
                          component="div"
                          count={sharedDebtRows.length}
                          page={debtPage}
                          onPageChange={(_, page) => setDebtPage(page)}
                          rowsPerPage={5}
                          rowsPerPageOptions={[5]}
                          onRowsPerPageChange={() => {}}
                        />
                      </Box>
                    )}
                  </Box>
                ) : null}
                {recordType === "account" ? (
                  <Box>
                    {accountLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Loading account items...
                        </Typography>
                      </Stack>
                    ) : sharedAccountRows.length === 0 ? (
                      <Typography color="text.secondary">
                        No items shared with this group yet.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Account</TableCell>
                            <TableCell align="right">Share</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sharedAccountRows.map((row) => {
                            const shared = Boolean(
                              isAccountSharedByRecordId(row.id)
                            );
                            return (
                              <TableRow key={row.id}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell align="right">
                                  <Switch
                                    checked={isShareAllOn ? true : shared}
                                    disabled={isShareAllOn}
                                    onChange={(_, checked) => {
                                      const account =
                                        accountItems.find(
                                          (item) => item.account_id === row.id
                                        ) ||
                                        ({
                                          account_id: row.id,
                                          item_id: "",
                                          balances: {
                                            available: null,
                                            current: null,
                                            limit: null,
                                          },
                                          mask: "",
                                          name: row.name,
                                          official_name: null,
                                          subtype: "unknown",
                                          type: "depository",
                                        } as PlaidAccount);
                                      void toggleAccountShare(account, checked);
                                    }}
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#14b8a6",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                          backgroundColor: "#14b8a6",
                                        },
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                ) : null}
                {recordType === "goal" || recordType === "transaction" ? (
                  <SharedRecordsView
                    records={records}
                    onUnshare={isShareAllOn ? undefined : handleUnshare}
                    unsharingId={unsharingId}
                    showTitle={false}
                    groupByLabel={false}
                    showRecordType={recordType !== "goal"}
                    showPermission={recordType !== "goal"}
                  />
                ) : null}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      );
    },
    [
      expandedSections,
      handleUnshare,
      loadSection,
      loadIncomeItems,
      loadExpenseItems,
      loadDebtItems,
      loadAccountItems,
      sectionLoading,
      sectionRecords,
      shareAll,
      shareAllEnabled,
      shareAllLoading,
      incomeLoading,
      expenseLoading,
      debtLoading,
      accountLoading,
      groupedIncomeItems,
      sharedIncomeRows,
      groupedExpenseItems,
      sharedDebtRows,
      pagedSharedDebtRows,
      sharedAccountRows,
      incomeGroupExpanded,
      incomeGroupLoading,
      isIncomeSharedByRecordId,
      isExpenseSharedByRecordId,
      isDebtSharedByRecordId,
      isAccountSharedByRecordId,
      toggleIncomeGroupShare,
      toggleIncomeShare,
      expenseGroupExpanded,
      expenseGroupLoading,
      toggleExpenseShare,
      toggleExpenseGroupShare,
      debtItems,
      toggleDebtShare,
      accountItems,
      toggleAccountShare,
      unsharingId,
      debtPage,
    ]
  );

  const sections = useMemo(
    () => [
      renderSection("Income", "income"),
      renderSection("Expenses", "expense"),
      renderSection("Debts", "debt"),
      renderSection("Goals", "goal"),
      renderSection(
        "Accounts",
        "account",
        "Includes Transactions for shared accounts."
      ),
      <Accordion
        key="tools-profiles"
        disableGutters
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          "&:before": { display: "none" },
          boxShadow: "none",
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Tools & Profiles
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Toggle Sharing All
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {toolProfilesLoading ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ py: 1 }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading profiles...
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.25}>
              {toolSections.map((section) => (
                <Box
                  key={section.key}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 0.75 }}
                  >
                    {section.label}
                  </Typography>
                  <Stack spacing={0.5}>
                    {(toolProfiles[section.key] || []).map((profile) => (
                      <FormControlLabel
                        key={profile.id}
                        control={
                          <Switch
                            checked={Boolean(
                              toolProfileShares[section.key]?.[profile.id]
                            )}
                            onChange={(_, checked) => {
                              if (section.key === "shoppingLists") {
                                void toggleShoppingListShare(
                                  profile.id,
                                  checked
                                );
                                return;
                              }
                              toggleToolProfileShare(
                                section.key,
                                profile.id,
                                checked
                              );
                            }}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#14b8a6",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: "#14b8a6",
                                },
                            }}
                          />
                        }
                        label={profile.name}
                      />
                    ))}
                    {(toolProfiles[section.key] || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No profiles yet.
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>,
    ],
    [
      renderSection,
      toggleToolProfileShare,
      toggleShoppingListShare,
      toolProfileShares,
      toolProfiles,
      toolProfilesLoading,
      toolSections,
    ]
  );

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={800}>
            Shared Items
          </Typography>
          <Typography color="text.secondary">
            Share everything by section (granular sharing can be added later).
          </Typography>
        </Box>
        <Stack spacing={2}>{sections}</Stack>
      </Stack>
      <UnshareConfirmModal
        open={Boolean(confirmShareId)}
        onClose={() => setConfirmShareId(null)}
        onConfirm={confirmUnshare}
      />
    </Container>
  );
};

export default GroupSharedPage;
